import { AxiosError } from "axios";
import { useAtom } from "jotai";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ButtonLink, SubmitButton } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { AuthenticatorSmsConfirmForm } from "@/components/Welcome/AuthenticatorSetup/AuthenticatorSmsConfirmForm";
import {
  CONFIRMATION_CODE_LENGTH,
  TwoFactorConfirmationCodeForm,
} from "@/components/Welcome/AuthenticatorSetup/TwoFactorConfirmationInput";
import { useAlert } from "@/hooks/useAlert";
import { twoFactorPhoneNumberAtom } from "@/hooks/useRequestTwoFactorPhoneNumber";
import {
  useResendActionForSmsSetup,
  useResendForSmsPhonenumberChange,
} from "@/hooks/useResendSms";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useValidateTwoFactorAuthentication } from "@/services/auth";
import {
  ChangeTwoFactorAuthenticationMethod,
  ValidateTwoFactorAuthenticationMethod,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";

import { expectedAuthTypeQueryParam } from "./TwoFactorAuthStatus";

// TODO: This component is very similar to AuthenticatorSmsConfirm.tsx
/**
 * Confirmation screen for both the 2FA SMS setup and the 2FA phone number change.
 */
export const TwoFactorSmsConfirm = ({
  useResendAction,
}: {
  useResendAction:
    | typeof useResendForSmsPhonenumberChange
    | typeof useResendActionForSmsSetup;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useAlert();
  const formMethods = useForm<TwoFactorConfirmationCodeForm>({
    defaultValues: {
      code: "",
    },
  });
  const code = useWatch({ control: formMethods.control, name: "code" });
  const [, setLocalPhoneNumber] = useAtom(twoFactorPhoneNumberAtom);
  const { mutateAsync: validateTwoFactorAuth } =
    useValidateTwoFactorAuthentication();

  const resendAction = useResendAction();

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit((data) => {
          validateTwoFactorAuth({
            params: {
              code: parseInt(data.code),
              method: ValidateTwoFactorAuthenticationMethod.SMS,
            },
          })
            .then(() => {
              setLocalPhoneNumber((data) => ({ ...data, confirmed: true }));
              showToast({
                text: t(
                  "portal:user-account.security.2fa.alerts.smsSetupSuccess",
                ),
                type: AlertTypes.success,
              });
              const queryParams = new URLSearchParams();
              queryParams.set(
                expectedAuthTypeQueryParam,
                ChangeTwoFactorAuthenticationMethod.SMS,
              );
              navigate(portalRoutes.user.security.base + `?${queryParams}`);
            })
            .catch(
              (
                error: AxiosError<{
                  errorCode: number;
                  message: string;
                }>,
              ) => {
                if (
                  error.response?.status === 400 &&
                  error.response.data?.errorCode === 402
                ) {
                  formMethods.setError("code", {
                    type: "manual",
                    message: t(
                      "portal:authenticator.app.setup.step2.invalidCode",
                    ),
                  });
                }
              },
            );
        })}
      >
        <ProfileCard
          maxWidth="none"
          title={t("portal:user-account.security.2fa.initialSetup.title")}
          actions={
            <>
              <ButtonLink variant="secondary" size="sm" to={-1 as any}>
                {t("portal:user-account.security.buttons.back")}
              </ButtonLink>
              <SubmitButton
                fullWidth={false}
                size="sm"
                label={t("portal:user-account.security.buttons.verify")}
                disabled={code.length < CONFIRMATION_CODE_LENGTH}
              />
            </>
          }
        >
          <AuthenticatorSmsConfirmForm resendAction={resendAction} />
        </ProfileCard>
      </form>
    </FormProvider>
  );
};
